import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { docsApi } from "./docs-api";
import { docsQueries } from "./docs-keys";
import type { CreateDocumentInput, DocumentUpdate } from "@shared/types/supabase.types";

export function useCreateDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateDocumentInput) => docsApi.createDocument(input),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: docsQueries.byContainer(data.parent_container_id ?? "").queryKey,
      });
    },
  });
}

export function useUpdateDocument(userId: string, parentContainerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      docId,
      updates,
    }: {
      docId: string;
      updates: DocumentUpdate;
    }) => docsApi.updateDocument(userId, parentContainerId, docId, updates),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: docsQueries.byDocumentId(data.id).queryKey,
      });
      await queryClient.invalidateQueries({
        queryKey: docsQueries.byContainer(data.parent_container_id ?? "").queryKey,
      });
    },
  });
}

export function useDocumentsByContainerQuery(containerId: string | undefined) {
  return useQuery({
    ...docsQueries.byContainer(containerId!),
    enabled: !!containerId,
    staleTime: 60 * 1000,
  });
}

export function useDeleteDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (docId: string) => docsApi.deleteDocument(docId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["documents"],
      });
    },
  });
}
